// @ts-strict-ignore
import {ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, OnChanges, Output, SimpleChanges} from "@angular/core";
import {ActivityState, ConnectivityState, Media, MediaLibraryPanelData, Mediaset, MediaType, PlayerMapping} from "@models";
import {SimMediasetService, SimRecorderService, SimUtilityExtendedService} from "@services";
import {Translations} from "@translations";
import Enumerable from "linq";
import dayjs from "dayjs";
import {debounce} from "utils-decorators";

@Component({
	selector: "sim-media-library-panel",
	templateUrl: "./media-library-panel.component.html",
	styleUrls: ["./media-library-panel.component.scss"],
	standalone: false,
})
export class MediaLibraryPanelComponent implements OnChanges, DoCheck {
	T = Translations;
	MediaType = MediaType;
	ActivityState = ActivityState;

	private volumeChangedDate: Date = null;
	volume: number = 100;
	private lastVolume: number;
	hasMediaWithAudio: boolean = false;

	@Input() mediaSetId: string;
	@Input() mediaPlayer: PlayerMapping;

	@Output() toggleMedia = new EventEmitter<{
		player: PlayerMapping;
		media: Media;
	}>();
	@Output() volumeUpdated = new EventEmitter<number>();

	public mediaSet: Mediaset;

	ConnectivityState = ConnectivityState;

	constructor(
		private _simUtilityService: SimUtilityExtendedService,
		private _simMediasetService: SimMediasetService,
		private changeDetector: ChangeDetectorRef,
		private _simRecorderService: SimRecorderService,
	) {}

	ngDoCheck() {
		if (!!this.mediaPlayer?.info?.currentMedia && this.volume !== this.mediaPlayer?.info?.currentMedia?.volume) {
			if (!this.volumeChangedDate || this.volumeChangedDate < dayjs(new Date()).subtract(1, "seconds").toDate()) {
				this.volumeChangedDate = null;
				this.volume = this.mediaPlayer?.info?.currentMedia?.volume;
				this.changeDetector.markForCheck();
			}
		}
	}

	public async ngOnChanges(changes: SimpleChanges) {
		if (changes["mediaSetId"]) {
			if (this.mediaSetId) {
				this.mediaSet = await this._simMediasetService.getMediasetById(this.mediaSetId);

				if (this.mediaSet) {
					for (const media of this.mediaSet.medias) {
						media["url"] = this._simUtilityService.getMediaPath(media);
					}
				}

				this.hasMediaWithAudio = Enumerable.from(this.mediaSet.medias).any((x) =>
					[MediaType.Audio, MediaType.Video].includes(x.mediaType),
				);
			} else {
				this.mediaSet = null;
			}

			this.changeDetector.markForCheck();
		}
	}

	private updateVolumeOfCurrentMedia() {
		if (this.mediaPlayer?.info?.currentMedia) {
			this.volumeChangedDate = new Date();
			const result = this._simRecorderService.updateVolumeOfCurrentMedia({
				playerMappingId: this.mediaPlayer?.id,
				volume: this.volume,
			});

			if (result) {
				this.volumeUpdated.emit(this.volume);
			}
		}
	}

	@debounce(1000)
	volumeChanged(volume: number) {
		this.volume = volume;
		this.updateVolumeOfCurrentMedia();
	}

	mute() {
		this.lastVolume = this.volume;
		this.volume = 0;
		this.updateVolumeOfCurrentMedia();
	}

	unMute() {
		this.volume = this.lastVolume ?? 100;
		this.updateVolumeOfCurrentMedia();
	}

	public readonly MediaLibraryPanelData: MediaLibraryPanelData;
	public readonly AudioCardData: {listView: false};
}
